import { reset } from '@/assetGroupEditor/assetGroup.actions';
import { ITEM_TYPES } from '@/homescreen/homescreen.constants';
import { setAssetGroupEditorVisible, setStatisticsSummaryTabVisible } from '@/main/layout.actions';
import { setCalculationHierarchyInTab } from '@/main/layoutPreferences';
import { closeStoredTableEditor } from '@/storedTableEditor/storedTable.utilities';
import { closeTableEditor } from '@/tableDefinitionEditor/tableDefinition.utilities';
import { toggleFormulaHelp } from '@/workbench/workbench.actions';
import { setDisplayAssetGroupEditor } from '@/worksheet/worksheet.actions';
import type { IJsonBorderNode, IJsonModel } from 'flexlayout-react';

export const searchTabId = 'search';
export const investigateTabId = 'investigate';
export const journalTabId = 'annotate';
export const worksheetsTabId = 'worksheetsTab';
export const dataPanelTabId = 'search';
export const trendTabId = 'trendTabId';
export const statisticsSummaryTabId = 'statisticsSummaryTabId';
export const trendTabSetId = 'trendTabSetId';
export const detailsPanelTabId = 'detailsPanel';
export const toolsPanelTabId = 'investigate';
export const journalPanelTabId = 'annotate';
export const capsulePanelTabId = 'capsulePanelTabId';
export const formulaHelpTabId = 'formulaHelo';
export const propertiesTabId = 'reportConfig';
export const commentsTabId = 'reportComments';
export const editorTabId = 'editorTabId';
export const tableDefinitionTabId = 'tableDefinitionTabId';
export const storedTableTabId = 'storedTableTabId';
export const assetGroupTabId = 'assetGroupTabId';
export const calculationHierarchyTabId = 'calculationHierarchyTabId';
export const collapsibleSidePanelId = 'collapsibleSidePanelId';
export const vantageTableTabId = 'vantageTableTabId';
export const vantageContextTabId = 'vantageContextTabId';
export const vantageSettingsTabId = 'vantageSettingsTabId';
export const vantageActionsTabId = 'vantageActionsTabId';
export const vantageConditionMonitorTabId = 'vantageConditionMonitorTabId';
export const vantageMonitoredConditionsTabId = 'vantageMonitoredConditionsTabId';
export const vantageColumnConfigTabId = 'vantageColumnConfigTabId';
export const vantageVisualizeTabId = 'vantageVisualizeTabId';
export const vantageRunLogTabId = 'vantageRunLogTabId';
export const SIDE_PANEL_INITIAL_WIDTH = 373;

const getItemType = (isActionBoard: boolean, report: boolean) => {
  if (isActionBoard) {
    return ITEM_TYPES.ACTION_BOARD;
  }
  if (report) {
    return ITEM_TYPES.REPORT;
  }
  return ITEM_TYPES.ANALYSIS;
};

const globalConfig = {
  tabSetEnableSingleTabStretch: false,
  tabSetEnableDeleteWhenEmpty: true,
  tabSetMinWidth: 30,
  tabSetMinHeight: 100,
  splitterSize: 3,
  splitterExtra: 0,
  tabEnableClose: false,
  tabEnableRename: false,
};

const trendTabJson = {
  type: 'tab',
  id: trendTabId,
  name: 'LAYOUT.TREND_TAB',
  component: trendTabId,
  config: {
    icon: 'fc-trend',
    testId: trendTabId,
  },
};

export const statisticsSummaryTabJson = {
  type: 'tab',
  id: statisticsSummaryTabId,
  name: 'LAYOUT.STATISTICS_SUMMARY_TAB',
  component: statisticsSummaryTabId,
  config: {
    icon: 'fc-statistic-summary',
    testId: statisticsSummaryTabId,
    closeAction: 'CLOSE_STATISTICS_SUMMARY',
  },
};

export const journalTabJson = {
  type: 'tab',
  id: journalPanelTabId,
  name: 'LAYOUT.JOURNAL_TAB',
  component: journalPanelTabId,
  config: {
    icon: 'fa-book fa-regular',
    testId: journalTabId,
    clickAction: 'TABSET_CHANGE',
  },
};

const organizerConfigTabJson = {
  type: 'tab',
  id: propertiesTabId,
  name: 'LAYOUT.PROPERTIES_TAB',
  component: propertiesTabId,
  config: {
    icon: 'fa-cog fa-regular',
    testId: propertiesTabId,
    clickAction: 'TABSET_CHANGE',
  },
};

const organizerCommentsTabJson = {
  type: 'tab',
  id: commentsTabId,
  name: 'LAYOUT.COMMENTS_TAB',
  component: commentsTabId,
  config: {
    icon: 'fa-comment-dots fa-regular',
    testId: commentsTabId,
    clickAction: 'TABSET_CHANGE',
  },
};

const editorTabJson = {
  type: 'tab',
  id: editorTabId,
  name: 'LAYOUT.EDITOR_TAB',
  component: editorTabId,
  config: {
    icon: 'fa-pencil fa-regular',
  },
};

const dataPanelTabJson = {
  type: 'tab',
  id: dataPanelTabId,
  name: 'LAYOUT.DATA_TAB',
  component: dataPanelTabId,
  config: {
    icon: 'fa-database fa-regular',
    testId: searchTabId,
    clickAction: 'TABSET_CHANGE',
  },
};

const toolsPanelTabJson = {
  type: 'tab',
  id: toolsPanelTabId,
  name: 'LAYOUT.TOOLS_TAB',
  component: toolsPanelTabId,
  config: {
    icon: 'fa-wrench fa-regular',
    testId: investigateTabId,
    clickAction: 'TABSET_CHANGE',
  },
};

export const detailsPanelTabJson = {
  type: 'tab',
  id: detailsPanelTabId,
  name: 'LAYOUT.DETAILS_TAB',
  component: detailsPanelTabId,
  config: {
    icon: 'fa-chart-area',
  },
};

const worksheetsTabJson = (itemType: (typeof ITEM_TYPES)[keyof typeof ITEM_TYPES]) => {
  const getTabName = () => {
    if (itemType === ITEM_TYPES.ACTION_BOARD) return 'LAYOUT.BOARDS_TAB';
    if (itemType === ITEM_TYPES.REPORT) return 'LAYOUT.DOCUMENTS_TAB';
    return 'LAYOUT.WORKSHEETS_TAB';
  };

  return {
    type: 'tab',
    id: worksheetsTabId,
    name: getTabName(),
    component: worksheetsTabId,
    config: {
      icon: 'fa-files',
      testId: 'worksheetsTab',
      clickAction: 'TOGGLE_WORKSHEET',
    },
  };
};

export const assetGroupTabJson = {
  type: 'tab',
  id: assetGroupTabId,
  name: 'LAYOUT.ASSET_GROUP_EDITOR_TAB',
  component: assetGroupTabId,
  config: {
    icon: 'fa-cubes',
    closeAction: 'CLOSE_ASSET_GROUP',
  },
};

export const tableDefinitionTabJson = {
  type: 'tab',
  id: tableDefinitionTabId,
  name: 'LAYOUT.TABLE_DEFINITION_TAB',
  component: tableDefinitionTabId,
  config: {
    icon: 'fa-table',
    closeAction: 'CLOSE_TABLE_DEFINITION_EDITOR',
  },
};

export const storedTableTabJson = {
  type: 'tab',
  id: storedTableTabId,
  name: 'LAYOUT.STORED_TABLE_TAB',
  component: storedTableTabId,
  config: {
    icon: 'fa-table',
    closeAction: 'CLOSE_STORED_TABLE_EDITOR',
  },
};

export const BORDER_WIDTH = 122;
export const BORDER_TAB_HEIGHT = 35;
const bordersJSON = (report: boolean, isActionBoard = false): IJsonBorderNode[] => [
  {
    type: 'border',
    selected: 0,
    location: 'left',
    minSize: 30,
    size: BORDER_WIDTH,
    children: [worksheetsTabJson(getItemType(isActionBoard, report))],
  },
];

export const organizerLayoutJson: IJsonModel = {
  global: globalConfig,
  borders: bordersJSON(true),
  layout: {
    type: 'row',
    id: 'root',
    children: [
      {
        type: 'tabset',
        minWidth: 0,
        id: collapsibleSidePanelId,
        children: [organizerConfigTabJson, organizerCommentsTabJson],
      },
      {
        type: 'tabset',
        id: 'editorTabSetId',
        children: [editorTabJson],
      },
    ],
  },
};
export const actionBoardTabId = 'actionBoardTabId';

const actionBoardTabJson = {
  type: 'tab',
  id: actionBoardTabId,
  name: 'LAYOUT.BOARDS_TAB',
  component: actionBoardTabId,
  config: {
    icon: 'fa-sparkles',
  },
};

export const actionBoardLayoutJson: IJsonModel = {
  global: globalConfig,
  borders: bordersJSON(false, true),
  layout: {
    type: 'row',
    id: 'root',
    children: [
      {
        type: 'tabset',
        id: 'actionBoardTabSetId',
        enableTabStrip: false,
        children: [actionBoardTabJson],
      },
    ],
  },
};
export const worksheetLayoutJSON: IJsonModel = {
  global: globalConfig,
  borders: bordersJSON(false),
  layout: {
    type: 'row',
    id: 'root',
    children: [
      {
        type: 'tabset',
        minWidth: 0,
        id: collapsibleSidePanelId,
        children: [dataPanelTabJson, toolsPanelTabJson, journalTabJson],
      },
      {
        type: 'row',
        id: 'trendarea',
        children: [
          {
            type: 'tabset',
            id: trendTabSetId,
            weight: 130,
            children: [trendTabJson, statisticsSummaryTabJson],
          },
          {
            type: 'tabset',
            weight: 50,
            children: [detailsPanelTabJson],
          },
        ],
      },
    ],
  },
};

export const vantageSettingsTabJson = {
  type: 'tab',
  id: vantageSettingsTabId,
  name: 'VANTAGE.SETTINGS.SETTINGS',
  component: vantageSettingsTabId,
  config: {
    icon: 'fa-gear fa-regular',
    testId: vantageSettingsTabId,
  },
};

export const vantageActionsTabJson = {
  type: 'tab',
  id: vantageActionsTabId,
  name: 'VANTAGE.ACTIONS.ACTIONS',
  component: vantageActionsTabId,
  config: {
    icon: 'fa-person-running',
    testId: vantageActionsTabId,
  },
};

export const vantageConditionMonitorTabJson = {
  type: 'tab',
  id: vantageConditionMonitorTabId,
  name: 'VANTAGE.CONDITION_CONFIG.TITLE',
  component: vantageConditionMonitorTabId,
  enableClose: true,
  config: {
    icon: 'fc-capsule-set fa-regular',
    testId: vantageConditionMonitorTabId,
  },
};

export const vantageMonitoredConditionsTabJson = {
  type: 'tab',
  id: vantageMonitoredConditionsTabId,
  name: 'VANTAGE.MONITORED_CONDITIONS.TITLE',
  component: vantageMonitoredConditionsTabId,
  enableClose: true,
  config: {
    icon: 'fa-list-check fa-regular',
    testId: vantageMonitoredConditionsTabId,
  },
};

export const vantageColumnConfigTabJson = {
  type: 'tab',
  id: vantageColumnConfigTabId,
  name: 'VANTAGE.SETTINGS.COLUMNS',
  component: vantageColumnConfigTabId,
  enableClose: true,
  config: {
    icon: 'fa-columns-3 fa-regular',
    testId: vantageColumnConfigTabId,
  },
};

export const vantageTableTabJson = {
  type: 'tab',
  id: vantageTableTabId,
  name: 'VANTAGE.EVIDENCE_TABLE.TITLE',
  component: vantageTableTabId,
  config: {
    icon: 'fc-vantage fa-regular',
    testId: vantageTableTabId,
  },
};

export const vantageContextTabJson = {
  type: 'tab',
  id: vantageContextTabId,
  name: 'VANTAGE.CONTEXT_PANEL.TITLE',
  component: vantageContextTabId,
  config: {
    icon: 'fa-rectangle-history',
    testId: vantageContextTabId,
  },
};

export const vantageVisualizeTabJson = {
  type: 'tab',
  id: vantageVisualizeTabId,
  name: 'VANTAGE.VISUALIZE',
  component: vantageVisualizeTabId,
  config: {
    icon: 'fa-sparkles',
    testId: vantageVisualizeTabId,
  },
};

export const vantageRunLogTabJson = {
  type: 'tab',
  id: vantageRunLogTabId,
  name: 'VANTAGE.RUN_LOG.TITLE',
  component: vantageRunLogTabId,
  enableClose: true,
  config: {
    icon: 'fa-clock-rotate-left',
    testId: vantageRunLogTabId,
  },
};

export const vantageLayoutJson: IJsonModel = {
  global: globalConfig,
  borders: [
    {
      type: 'border',
      selected: 0,
      location: 'left',
      minSize: 30,
      size: 122,
      children: [vantageSettingsTabJson, vantageActionsTabJson],
    },
  ],
  layout: {
    type: 'row',
    id: 'root',
    weight: 100,
    children: [
      {
        type: 'tabset',
        id: 'mainTab',
        weight: 70,
        enableClose: false,
        enableDrop: false,
        selected: 0,
        children: [vantageTableTabJson, journalTabJson],
      },
      {
        type: 'tabset',
        id: 'contextTab',
        weight: 30,
        enableClose: false,
        enableDrop: false,
        children: [vantageContextTabJson],
      },
    ],
  },
};

export const vantageEmbeddedLayoutJson: IJsonModel = {
  global: globalConfig,
  layout: {
    type: 'row',
    id: 'root',
    weight: 100,
    children: [
      {
        type: 'tabset',
        id: 'mainTab',
        weight: 70,
        enableClose: false,
        enableDrop: false,
        selected: 0,
        children: [vantageTableTabJson],
      },
      {
        type: 'tabset',
        id: 'contextTab',
        weight: 30,
        minWidth: 0,
        enableClose: false,
        enableDrop: false,
        children: [vantageContextTabJson],
      },
    ],
  },
};

export const mobileOrganizerLayoutJSON: IJsonModel = {
  global: globalConfig,
  borders: [
    {
      type: 'border',
      selected: -1,
      location: 'left',
      size: 122,
      children: [worksheetsTabJson(ITEM_TYPES.REPORT), organizerConfigTabJson, organizerCommentsTabJson],
    },
  ],
  layout: {
    type: 'row',
    id: 'root',
    children: [
      {
        type: 'tabset',
        id: 'editorTabSetId',
        children: [editorTabJson],
      },
    ],
  },
};
export const mobileWorksheetLayoutJSON: IJsonModel = {
  global: globalConfig,
  borders: [
    {
      type: 'border',
      selected: -1,
      location: 'left',
      size: 122,
      children: [worksheetsTabJson(ITEM_TYPES.ANALYSIS), dataPanelTabJson, toolsPanelTabJson, journalTabJson],
    },
  ],
  layout: {
    type: 'row',
    id: 'root',
    children: [
      {
        type: 'row',
        children: [
          {
            type: 'tabset',
            id: trendTabSetId,
            weight: 130,
            children: [trendTabJson, statisticsSummaryTabJson],
          },
          {
            type: 'tabset',
            weight: 50,
            children: [detailsPanelTabJson],
          },
        ],
      },
    ],
  },
};
export const capsulePanelTabJson = {
  type: 'tab',
  id: capsulePanelTabId,
  name: 'LAYOUT.CAPSULES_TAB',
  component: capsulePanelTabId,
  config: {
    icon: 'fc-capsule',
  },
};

export const formulaHelpTabConfig = {
  icon: 'fa-info-circle',
  customPosition: false,
  closeAction: 'CLOSE_FORMULA_HELP',
};
export const formulaHelpTabJson = {
  type: 'tab',
  id: formulaHelpTabId,
  name: 'LAYOUT.FORMULA_HELP_TAB',
  component: formulaHelpTabId,
  config: formulaHelpTabConfig,
};

export const calculationHierarchyTabJson = {
  type: 'tab',
  id: calculationHierarchyTabId,
  name: 'LAYOUT.CALCULATION_HIERARCHY_TAB',
  component: calculationHierarchyTabId,
  config: {
    icon: 'fc-asset-tree',
    closeAction: 'CLOSE_CALCULATION_HIERARCHY',
  },
};

export const closeActionLookup: Record<string, () => void> = {
  CLOSE_ASSET_GROUP: () => {
    setDisplayAssetGroupEditor(false);
    reset();
    setAssetGroupEditorVisible(false);
  },
  CLOSE_TABLE_DEFINITION_EDITOR: () => {
    closeTableEditor('SCALING');
  },
  CLOSE_ITEM_TABLE_EDITOR: () => {
    closeTableEditor('ITEM');
  },
  CLOSE_STORED_TABLE_EDITOR: () => {
    closeStoredTableEditor();
  },
  CLOSE_FORMULA_HELP: () => {
    toggleFormulaHelp();
  },
  CLOSE_CALCULATION_HIERARCHY: () => {
    setCalculationHierarchyInTab(false);
  },
  CLOSE_STATISTICS_SUMMARY: () => {
    setStatisticsSummaryTabVisible(false);
  },
};
